import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Collapse,
  Link,
} from "@mui/material";
import { keyframes } from "@emotion/react";
import { useState } from "react";
import { CompanyPointSource } from "@t3/shared";
import { usePrpNavigation } from "./PrpNavigationContext";

// Glimmer animation keyframes with very long pause
const glimmer = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-100%);
  }
  12.5% {
    opacity: 1;
    transform: translateX(100%);
  }
  100% {
    opacity: 1;
    transform: translateX(100%);
  }
`;

interface PointSourceBadgeProps {
  sourceKey: string;
  amount: number;
  companyPointSources: CompanyPointSource;
  index: number;
  totalSources: number;
  onClick?: () => void;
}

interface PointSourcesBadgesProps {
  userPointSources: { [key: string]: number };
  companyPointSources: CompanyPointSource;
}

export const PointSourceBadge: React.FC<PointSourceBadgeProps> = ({
  sourceKey,
  amount,
  companyPointSources,
  index,
  totalSources,
  onClick,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const sourceConfig = companyPointSources[sourceKey];
  const displayName = sourceConfig?.name || sourceKey;
  const colors = sourceConfig?.colors;
  const logo = sourceConfig?.logo;

  // Calculate glimmer intensity based on ranking (higher rank = more intense)
  const rankIntensity = (totalSources - index) / totalSources;
  const glimmerOpacity = rankIntensity * 0.75 + 0.15;

  return (
    <Box
      onClick={onClick ? onClick : undefined}
      sx={{
        display: "inline-flex",
        flexDirection: "column",
        backgroundColor: colors?.primary || theme.palette.grey[100],
        border: `2px solid ${colors?.secondary || theme.palette.grey[400]}`,
        borderRadius: 0,
        boxShadow: colors?.primary
          ? `0 0 12px ${colors.primary}55, 0 2px 8px ${colors.primary}55`
          : `0 0 8px ${theme.palette.grey[300]}66, 0 2px 6px ${theme.palette.grey[400]}33`,
        backdropFilter: "blur(1px)",
        width: isMobile ? "100%" : "auto",
        minWidth: logo ? "auto" : "120px",
        maxWidth: isMobile ? "300px" : "350px",
        position: "relative",
        overflow: "hidden",
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          transform: onClick ? "translateY(-2px)" : "none",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(
            120deg,
            transparent 30%,
            rgba(255, 255, 255, ${glimmerOpacity}) 50%,
            transparent 70%
          )`,
          width: "200%",
          height: "100%",
          animation: `${glimmer} 12.8s infinite forwards`,
          opacity: 0,
          animationDelay: `${index * 0.4}s`,
          pointerEvents: "none",
          zIndex: 2,
        },
      }}
    >
      {/* Logo Section */}
      {logo && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: isMobile ? "60px" : "50px",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            borderRadius: "0",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box
            component="img"
            src={logo}
            alt={displayName}
            sx={{
              maxWidth: "100%",
              width: "auto",
              height: "auto",
              objectFit: "contain",
            }}
          />
        </Box>
      )}

      {/* Content Section */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "6px 12px",
          gap: 1,
          position: "relative",
          zIndex: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: isMobile ? "1.2rem" : "1.3rem",
            fontWeight: 500,
            color: colors?.primary
              ? theme.palette.getContrastText(colors.primary)
              : theme.palette.text.primary,
            textShadow: "0 1px 2px rgba(0,0,0,0.1)",
            textAlign: "left",
            fontStyle: "italic",
            lineHeight: 1.2,
            flex: 1,
          }}
        >
          {displayName}
        </Typography>

        <Typography
          sx={{
            fontSize: isMobile ? "2.2rem" : "2.1rem",
            fontWeight: 800,
            textAlign: "right",
            background: `radial-gradient(circle at 150% 150%, ${colors.tertiary}, ${colors.secondary} 80%)`,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
            lineHeight: 1,
            whiteSpace: "nowrap",
          }}
        >
          {amount.toLocaleString()}
        </Typography>
      </Box>
    </Box>
  );
};

export const PointSourcesBadges: React.FC<PointSourcesBadgesProps> = ({
  userPointSources,
  companyPointSources,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [showAllSources, setShowAllSources] = useState(false);
  const { handleNavChange } = usePrpNavigation();

  // Sort point sources by amount (largest first) and filter out zero values
  const sortedPointSources = Object.entries(userPointSources)
    .filter(([_, amount]) => amount > 0)
    .sort(([, amountA], [, amountB]) => amountB - amountA);

  if (sortedPointSources.length === 0) {
    return null;
  }

  // Split sources into top 2 and remaining
  const topTwoSources = sortedPointSources.slice(0, 2);
  const remainingSources = sortedPointSources.slice(2);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography
        variant="subtitle2"
        sx={{
          mb: 1,
          color: "text.primary",
          fontWeight: 500,
          fontSize: "1.1rem",
        }}
      >
        Lifetime Rewards
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          flexWrap: isMobile ? "nowrap" : "wrap",
          gap: 1,
          alignItems: isMobile ? "stretch" : "center",
        }}
      >
        {topTwoSources.map(([sourceKey, amount], index) => {
          const sourceConfig = companyPointSources[sourceKey];
          const displayName = sourceConfig?.name || sourceKey;

          return (
            <PointSourceBadge
              key={sourceKey}
              sourceKey={sourceKey}
              amount={amount}
              companyPointSources={companyPointSources}
              index={index}
              totalSources={sortedPointSources.length}
              onClick={() =>
                handleNavChange({
                  path: `/shop/history?pointSource=${encodeURIComponent(sourceKey)}`,
                  label: displayName,
                })
              }
            />
          );
        })}

        {/* Collapsed additional sources */}
        <Collapse in={showAllSources} timeout={300}>
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              flexWrap: isMobile ? "nowrap" : "wrap",
              gap: 1,
              alignItems: isMobile ? "stretch" : "center",
              mb: 1,
            }}
          >
            {remainingSources.map(([sourceKey, amount], index) => {
              const sourceConfig = companyPointSources[sourceKey];
              const displayName = sourceConfig?.name || sourceKey;

              return (
                <PointSourceBadge
                  key={sourceKey}
                  sourceKey={sourceKey}
                  amount={amount}
                  companyPointSources={companyPointSources}
                  index={index + 2} // Adjust index to account for top two sources
                  totalSources={sortedPointSources.length}
                  onClick={() =>
                    handleNavChange({
                      path: `/shop/history?pointSource=${encodeURIComponent(sourceKey)}`,
                      label: displayName,
                    })
                  }
                />
              );
            })}
          </Box>
        </Collapse>
      </Box>

      {/* View More / Hide Controls */}
      {remainingSources.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {!showAllSources ? (
            <Link
              component="button"
              variant="subtitle2"
              onClick={() => setShowAllSources(true)}
              sx={{
                color: "text.secondary",
                fontWeight: 500,
                fontSize: "0.875rem",
                textDecoration: "none",
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                  color: "primary.main",
                },
              }}
            >
              show{" "}
              {remainingSources
                .slice(0, 2)
                .map(([sourceKey, amount]) => {
                  const sourceConfig = companyPointSources[sourceKey];
                  const displayName = sourceConfig?.name || sourceKey;
                  return `${displayName}: ${amount.toString()}`;
                })
                .join(", ")}
              {remainingSources.length > 2 &&
                `, and ${remainingSources.length - 2} more...`}
            </Link>
          ) : (
            <Link
              component="button"
              variant="subtitle2"
              onClick={() => setShowAllSources(false)}
              sx={{
                color: "text.secondary",
                fontWeight: 500,
                fontSize: "0.875rem",
                textDecoration: "none",
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                  color: "primary.main",
                },
              }}
            >
              hide contributors
            </Link>
          )}
        </Box>
      )}
    </Box>
  );
};
